import { DateInputFormik } from '@strata/react-date-time';
import { FormContainer } from '@strata/react-form-container';
import { TextAreaFormik, TextInputFormik } from '@strata/react-inputs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import { useNavigate } from 'react-router';
import * as Yup from 'yup';

import { postDeclareIncident } from 'api/oopsAPI';
import { handleError } from 'services/Error';
import { AppRoutes } from 'services/routes';
import {
    useGetImpactedServiceNamesQuery,
    useGetMissingQualityChecksQuery,
} from 'store/api/oopsApi';

const OTHER_OPTION = 'Other';
// eslint-disable-next-line no-secrets/no-secrets -- Form field name for custom "Other" value
const FIELD_OTHER = 'missingQualityCheckOther';
const FIELD_PRIMARY_IMPACTED_SERVICE_OTHER = 'primaryImpactedServiceNameOther';

const ValidationSchema = Yup.object({
    title: Yup.string().required(validationStrings.requiredField.errorMessage),
    startedAt: DateInputFormik.validator().required(validationStrings.requiredField.errorMessage),
    description: Yup.string().required(validationStrings.requiredField.errorMessage),
    primaryImpactedServiceName: Yup.string().required(validationStrings.requiredField.errorMessage),
    [FIELD_PRIMARY_IMPACTED_SERVICE_OTHER]: Yup.string().when('primaryImpactedServiceName', {
        is: OTHER_OPTION,
        then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
        otherwise: (schema) => schema.notRequired(),
    }),
    missingQualityCheck: Yup.string().required(validationStrings.requiredField.errorMessage),
    [FIELD_OTHER]: Yup.string().when('missingQualityCheck', {
        is: OTHER_OPTION,
        then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
        otherwise: (schema) => schema.notRequired(),
    }),
});

type DeclareIncidentFormValues = Yup.InferType<typeof ValidationSchema>;

export const DeclareIncident = () => {
    const navigate = useNavigate();
    const { data: missingQualityChecksFromApi = [], isFetching: missingQualityChecksFetching } =
        useGetMissingQualityChecksQuery(undefined, { refetchOnMountOrArgChange: true });
    const missingQualityChecks = [...missingQualityChecksFromApi, OTHER_OPTION];
    const { data: impactedServiceNamesFromApi = [], isFetching: impactedServiceNamesFetching } =
        useGetImpactedServiceNamesQuery(undefined, { refetchOnMountOrArgChange: true });
    const impactedServiceNames = [...impactedServiceNamesFromApi, OTHER_OPTION];

    return (
        <Formik<DeclareIncidentFormValues>
            initialValues={
                {
                    title: '',
                    startedAt: undefined,
                    description: '',
                    missingQualityCheck: undefined,
                    primaryImpactedServiceName: undefined,
                    [FIELD_OTHER]: '',
                    [FIELD_PRIMARY_IMPACTED_SERVICE_OTHER]: '',
                } as unknown as DeclareIncidentFormValues
            }
            validationSchema={ValidationSchema}
            onSubmit={async (values) => {
                try {
                    const startedAtDate = dayjs(values.startedAt);
                    const startedAtIso = startedAtDate.isValid() ? startedAtDate.toISOString() : '';

                    const formValues: DeclareIncidentFormValues & {
                        [FIELD_OTHER]: string;
                        [FIELD_PRIMARY_IMPACTED_SERVICE_OTHER]: string;
                    } = values as DeclareIncidentFormValues & {
                        [FIELD_OTHER]: string;
                        [FIELD_PRIMARY_IMPACTED_SERVICE_OTHER]: string;
                    };
                    const missingQualityCheckValue =
                        formValues.missingQualityCheck === OTHER_OPTION
                            ? formValues[FIELD_OTHER]
                            : formValues.missingQualityCheck;
                    const primaryImpactedServiceNameValue =
                        formValues.primaryImpactedServiceName === OTHER_OPTION
                            ? formValues[FIELD_PRIMARY_IMPACTED_SERVICE_OTHER]
                            : formValues.primaryImpactedServiceName;

                    await postDeclareIncident({
                        name: values.title,
                        started_at: startedAtIso,
                        description: values.description,
                        missing_quality_check: missingQualityCheckValue,
                        primary_impacted_service_name: primaryImpactedServiceNameValue,
                    });
                    SuccessToast({ message: 'Incident declared.' });
                    navigate(AppRoutes.viewIncidents);
                } catch (err) {
                    handleError('Error declaring incident.', err as Error);
                }
            }}
        >
            {(formik) => (
                <FormContainer
                    formTitle="Declare Incident"
                    primaryButtonLabel="Declare Incident"
                    onFormSubmit={formik.handleSubmit}
                    isSubmitting={formik.isSubmitting || !(formik.dirty && formik.isValid)}
                    onCancelClick={() => navigate(AppRoutes.viewIncidents)}
                    formLeftContent={
                        <>
                            <TextInputFormik id="title" label="Incident Title" required />
                            <DateInputFormik id="startedAt" label="Started At" showTime required />
                            <TextAreaFormik
                                id="description"
                                label="Description"
                                helperText="Provide details about the incident, including who reported the incident and how it was discovered."
                                required
                            />
                            <SuggestSingleSelectFormik
                                id="primaryImpactedServiceName"
                                label="Primary impacted service"
                                required
                                disableAutoPopulate
                                isLoading={impactedServiceNamesFetching}
                                values={impactedServiceNames}
                                selectedItem={
                                    impactedServiceNames.includes(
                                        formik.values.primaryImpactedServiceName ?? '',
                                    )
                                        ? formik.values.primaryImpactedServiceName
                                        : undefined
                                }
                                onChange={(newValue) => {
                                    void (async () => {
                                        await formik.setFieldValue(
                                            'primaryImpactedServiceName',
                                            newValue ?? undefined,
                                        );
                                        if (newValue !== OTHER_OPTION) {
                                            await formik.setFieldValue(
                                                FIELD_PRIMARY_IMPACTED_SERVICE_OTHER,
                                                '',
                                            );
                                        }
                                    })();
                                }}
                            />
                            {formik.values.primaryImpactedServiceName === OTHER_OPTION && (
                                <TextInputFormik
                                    id={FIELD_PRIMARY_IMPACTED_SERVICE_OTHER}
                                    label="Specify other primary impacted service"
                                    required
                                />
                            )}
                            <SuggestSingleSelectFormik
                                id="missingQualityCheck"
                                label="Missing quality check"
                                required
                                disableAutoPopulate
                                isLoading={missingQualityChecksFetching}
                                values={missingQualityChecks}
                                selectedItem={
                                    missingQualityChecks.includes(formik.values.missingQualityCheck)
                                        ? formik.values.missingQualityCheck
                                        : undefined
                                }
                                onChange={(newValue) => {
                                    void (async () => {
                                        await formik.setFieldValue(
                                            'missingQualityCheck',
                                            newValue ?? undefined,
                                        );
                                        if (newValue !== OTHER_OPTION) {
                                            await formik.setFieldValue(FIELD_OTHER, '');
                                        }
                                    })();
                                }}
                            />
                            {formik.values.missingQualityCheck === OTHER_OPTION && (
                                <TextInputFormik
                                    id={FIELD_OTHER}
                                    label="Specify other missing quality check"
                                    required
                                />
                            )}
                        </>
                    }
                />
            )}
        </Formik>
    );
};
